import {
  Add,
  ArrowBack,
  AttachFile,
  DescriptionOutlined,
  DownloadOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { downloadFile as downloadStoredFile } from "apis/controllers/files";
import fileDownload from "js-file-download";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  getApplicationExpenseMinutes,
  getExpenseApplicationDetails,
} from "src/apis/controllers/expenses";
import { formatFiles } from "src/util";
import FilePreviewer from "src/qubCommon/FilePreviewer";
import ExpenseBreadcrumbs from "../ExpenseBreadcrumbs";
import ExpenseMinutesDialog from "./ExpenseMinutesDialog";
import MhdExpensesHeader from "./MhdExpensesHeader";
import { getMinutePreviewType } from "./minutesFilePreview";

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("el-GR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const stripHtml = (value) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .trim();

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === "") return null;
  if (field?.type === "boolean") return value ? "Ναι" : "Όχι";
  if (field?.type === "dropdown") {
    return field?.options?.find((option) => option.value === value)?.label ?? value;
  }
  if (field?.type === "date") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat("el-GR").format(date);
    }
  }

  return String(value);
};

const toHistoryRows = (groups) =>
  groups
    .flatMap((group) =>
      (group?.bundles ?? []).map((bundle) => ({
        id: bundle.id,
        fields: group?.fields ?? [],
        typeTitle: stripHtml(group?.title),
        bundle,
      })),
    )
    .sort((left, right) => {
      const leftDate = new Date(left.bundle?.createdAt ?? 0).getTime();
      const rightDate = new Date(right.bundle?.createdAt ?? 0).getTime();
      return rightDate - leftDate;
    });

const ExpenseMinutesPage = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [availableGroups, setAvailableGroups] = useState([]);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [openingFileId, setOpeningFileId] = useState(null);
  const [fileToPreview, setFileToPreview] = useState(null);

  const loadMinutes = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const [response, detailsResponse] = await Promise.all([
        getApplicationExpenseMinutes(applicationId),
        getExpenseApplicationDetails(applicationId),
      ]);
      if (response?.status === "error" || response?.succeeded === false) {
        throw new Error(response?.message ?? "Δεν φορτώθηκαν τα Πρακτικά.");
      }
      if (
        detailsResponse?.status === "error" ||
        detailsResponse?.succeeded === false
      ) {
        throw new Error(
          detailsResponse?.message ?? "Δεν φορτώθηκαν τα στοιχεία της ΜΗΔ.",
        );
      }

      setAvailableGroups(formatFiles(response?.data ?? []) ?? []);
      setApplicationDetails(detailsResponse?.data ?? null);
    } catch (error) {
      setAvailableGroups([]);
      setApplicationDetails(null);
      setErrorMessage(error?.message ?? "Δεν φορτώθηκαν τα Πρακτικά.");
    } finally {
      setIsLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    loadMinutes();
  }, [loadMinutes]);

  const historyRows = useMemo(
    () => toHistoryRows(availableGroups),
    [availableGroups],
  );
  const manualMinuteGroups = useMemo(
    () =>
      availableGroups.filter(
        (group) => Number(group?.maxBundles ?? 0) > 0,
      ),
    [availableGroups],
  );

  const handleOpenFile = async (file) => {
    if (!file?.dbId) return;

    try {
      setOpeningFileId(file.dbId);
      const data = await downloadStoredFile(
        { Id: file.dbId },
        { responseType: "blob" },
      );
      const previewType = getMinutePreviewType(file);
      if (previewType) {
        setFileToPreview(new File([data], file.name || "Πρακτικό", { type: previewType }));
      } else {
        fileDownload(data, file.name || "praktiko");
      }
    } catch {
      enqueueSnackbar("Δεν ήταν δυνατό το άνοιγμα του αρχείου.", {
        variant: "error",
      });
    } finally {
      setOpeningFileId(null);
    }
  };

  return (
    <Box sx={{ m: 2.5 }}>
      <ExpenseBreadcrumbs
        applicationId={applicationId}
        applicationName={applicationDetails?.name}
        currentLabel="Πρακτικά"
      />
      <Stack spacing={2.5}>
        <MhdExpensesHeader applicationDetails={applicationDetails} compact />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/expenses/${applicationId}`)}
            >
              Επιστροφή στις δαπάνες
            </Button>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Ιστορικό Πρακτικών
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsDialogOpen(true)}
            disabled={isLoading || manualMinuteGroups.length === 0}
          >
            Νέο Πρακτικό
          </Button>
        </Stack>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {isLoading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={70} />
            <Skeleton variant="rounded" height={180} />
          </Stack>
        ) : historyRows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
            <DescriptionOutlined sx={{ fontSize: 44, color: "text.secondary" }} />
            <Typography variant="h6">Δεν υπάρχουν Πρακτικά</Typography>
            <Typography color="text.secondary">
              Επιλέξτε «Νέο Πρακτικό» για την πρώτη καταχώριση.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ minWidth: 960 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#e8f4fc" }}>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>
                    Ανάρτηση
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 170 }}>
                    Χρήστης
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>
                    Πηγή
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 320 }}>
                    Τύπος / Στοιχεία
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 190 }}>
                    Αρχεία
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.map(({ id, fields, typeTitle, bundle }) => {
                  const populatedFields = fields
                    .map((field) => ({
                      ...field,
                      displayValue: formatFieldValue(
                        field,
                        bundle?.values?.[field.name],
                      ),
                    }))
                    .filter((field) => field.displayValue !== null);

                  return (
                    <TableRow key={id} hover sx={{ verticalAlign: "top" }}>
                      <TableCell>{formatDateTime(bundle?.createdAt)}</TableCell>
                      <TableCell>{bundle?.createdBy || "—"}</TableCell>
                      <TableCell>
                        {bundle?.sourceType === "disbursement" &&
                        bundle?.sourceId ? (
                          <Link
                            component={RouterLink}
                            to={`/disbursement-application/${bundle.sourceId}`}
                            underline="hover"
                            sx={{ fontWeight: 600 }}
                          >
                            {bundle?.sourceLabel ?? "Αίτηση εκταμίευσης"}
                          </Link>
                        ) : (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={
                              bundle?.sourceLabel ?? "Αίτηση ΜΗΔ"
                            }
                            sx={{ maxWidth: "100%" }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={700}>
                            {typeTitle || "Αρχείο Πρακτικών"}
                          </Typography>
                          {populatedFields.length > 0 &&
                            populatedFields.map((field) => (
                              <Typography key={field.name} variant="body2">
                                <Box
                                  component="span"
                                  color="text.secondary"
                                  sx={{ mr: 0.5 }}
                                >
                                  {field.label}:
                                </Box>
                                <Box component="span" fontWeight={600}>
                                  {field.displayValue}
                                </Box>
                              </Typography>
                            ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.75} alignItems="flex-start">
                          {(bundle?.files ?? []).map((file) => (
                            <Link
                              key={file.id}
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => handleOpenFile(file)}
                              disabled={openingFileId !== null}
                              aria-label={`${getMinutePreviewType(file) ? "Προεπισκόπηση" : "Λήψη"} ${file.name || "αρχείου"}`}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                textAlign: "left",
                              }}
                            >
                              <AttachFile fontSize="small" />
                              <Box component="span">{file.name}</Box>
                              {openingFileId === file.dbId ? (
                                <CircularProgress size={14} />
                              ) : getMinutePreviewType(file) ? (
                                <VisibilityOutlined fontSize="small" />
                              ) : (
                                <DownloadOutlined fontSize="small" />
                              )}
                            </Link>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <FilePreviewer file={fileToPreview} onClose={() => setFileToPreview(null)} />
      <ExpenseMinutesDialog
        open={isDialogOpen}
        applicationId={applicationId}
        groups={manualMinuteGroups}
        onClose={() => setIsDialogOpen(false)}
        onSaved={loadMinutes}
      />
    </Box>
  );
};

export default ExpenseMinutesPage;
